import type { HTMLAttributes, ReactNode } from "react";
import { usePageUtilities } from "./page-utilities-context";

export interface PageLayoutProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  /**
   * Horizontal max-width cap. Defaults to 1760px, wide enough that
   * 1920 / 2560 monitors actually use their pixels instead of showing
   * a cramped 1100px column with dead strips on the side, narrow enough
   * that prose inside cards doesn't explode to 120ch+ lines.
   */
  maxWidth?: number;
}

/**
 * Canonical page wrapper for HealthOS surfaces. Every routed page (Today,
 * Plan, Community, Settings, ...) wraps its content in this so horizontal
 * and vertical rhythm stay identical across surfaces and the app feels like
 * one application at every breakpoint.
 *
 * Horizontal padding scale:
 *   - mobile: px-5
 *   - md (768+):  px-8
 *   - lg (1024+): px-10
 *   - xl (1280+): px-12
 *   - 2xl (1536+): px-16
 *
 * Vertical scale:
 *   - pt-12: clear space above the page header
 *   - pb-16: breathing room above the viewport bottom / bottom nav
 *
 * `text-ink-primary` restates the body default so a page keeps its ink even when
 * rendered inside a context that overrides colour.
 *
 * At xl the utilities dock absolutely over the page header. They sit at
 * z-30 so a page stacking context (for example Connectors' HeroReveal at
 * z-20, which keeps the privacy-eye tooltip above later sections) cannot
 * intercept clicks on search or the profile trigger.
 */
export function PageLayout({
  children,
  maxWidth = 1760,
  className,
  style,
  ...rest
}: PageLayoutProps) {
  const pageUtilities = usePageUtilities();
  return (
    <div
      {...rest}
      style={{ maxWidth, ...style }}
      className={[
        // A laptop viewport is short before it is narrow:
        "relative mx-auto w-full px-5 pt-12 pb-16 text-ink-primary md:px-8 lg:px-10 xl:px-12 2xl:px-16",
        "short:pt-6 short:pb-8",
        className ?? "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {pageUtilities ? (
        <div className="mb-4 flex min-h-11 items-center justify-end xl:absolute xl:right-12 xl:top-10 xl:z-30 xl:mb-0 2xl:right-16">
          {pageUtilities}
        </div>
      ) : null}
      {children}
    </div>
  );
}
